// =============================================================================
// Voice's budget read — issue #1932. The spoken half of get_budget.
//
// ⚠️ NOT TO BE CONFUSED WITH voiceWriteBudget.ts, which is about the per-window
// CAP on voice WRITES (#933). "Budget" is overloaded in this codebase: that file
// is a rate limit, this one is her money.
//
// ── Why this reuses kin-chat's read instead of querying ──────────────────────
//
// #1932's DONE WHEN says "totals must match the My Budget screen exactly". The
// strongest available form of that is not "imports the same helpers" — it is
// CALLING THE SAME FUNCTION. `fetchBudgetRead` is pure TypeScript with no Deno
// globals anywhere in its import graph (checked: budgetRead.ts, localDate,
// billExpiry, billPaycheckMatch, budgetNow, payDay), it takes its client by
// injection, and `src/` importing an Edge module is the direction this repo
// already uses — voiceScheduleRead.ts imports kin-chat/createEventWrite.ts, and
// voiceCreateEvent.ts runs kin-chat's event write outright.
//
// So voice, chat and the sheet cannot disagree about a number here, because
// there is only one place the numbers are computed. A second copy of this
// arithmetic is the defect #1923 and #1936 both existed to prevent.
//
// ── What is DIFFERENT for voice, and why it is not the numbers ───────────────
//
// Only the RENDERING. `renderBudgetRead` emits every project, every paycheck
// group and up to 40 named bills, which is right for a chat surface she can
// scroll. Out loud it is wrong twice over: it is unspeakable, and it invites the
// model to read a table aloud. The fix is to give the model LESS, not to ask it
// nicely to summarise — you cannot read aloud what you were never given. Same
// reasoning as voiceSchedule's caps.
//
// ── ⚠️ The honesty rules are inherited, not re-decided ──────────────────────
//
// Three outcomes stay apart all the way to the spoken payload, exactly as in
// budgetRead.ts's header: a FAILED read states no number; a TRUNCATED read's
// figures are floors and the "what's left" family is SUPPRESSED rather than
// annotated; and "no budget" is a real answer said plainly.
//
// ⚠️ Suppressed, not annotated. budgetRead.ts learned this in PR #1809's review
// and says so at its capped branch: a trailing warning cannot retract a number,
// because by the time the warning is read the actionable figure is already in
// the text. Out loud it is worse still, since she cannot scroll back to the
// caveat. So a capped read emits NO leftover, NO right-now and NO plan-on.
//
// ⚠️ And there is NO PER-PAYCHECK LEFTOVER here, for the reason PersonalBudget
// View's doc comment gives: those figures ignore transactions and undated
// bills, so they sum HIGHER than the month's real leftover ($2,190 against a
// true $2,000 on the worked fixture). An over-report is the one error that makes
// her spend money she does not have. The useful per-paycheck question is
// answered as a LIST — what is still owed before the next paycheck lands.
// =============================================================================

import {
  fetchBudgetRead,
  type BudgetReadOutcome,
  type PersonalBudgetView,
  type ProjectBudgetView,
} from "../../supabase/functions/kin-chat/budgetRead.ts";
import { DEFAULT_USER_TIMEZONE } from "../../supabase/functions/_shared/localDate.ts";
// ⚠️ Imported, not re-derived. It maps 32 to "Last day" (payDay.ts's LAST_DAY
// sentinel) as well as doing the ordinal, and a hand-rolled copy here would be
// the exact defect this file's header warns about — the first draft of this
// module had one, and it rendered day 32 as "32nd".
import { dueDayLabel } from "../../supabase/functions/_shared/payDay.ts";
// Her timezone resolved the way every other voice read resolves it, so "this
// month" and "today" mean the same thing in voice as on the screen.
import { userTimeZone } from "../../supabase/functions/kin-chat/createEventWrite.ts";

export interface VoiceBudgetClient {
  from: (table: string) => any;
}

/**
 * How many bills and budgets voice is GIVEN.
 *
 * The prompt asks Kin to name "the two or three biggest". She is given three
 * and a count of the rest, so "and four others" is a thing she can say truly
 * without having been handed four more names to read out.
 */
export const VOICE_BUDGET_NAMED = 3;

/**
 * ⚠️ Whole dollars, rounded in the direction that cannot cost her money.
 *
 * The prompt says whole dollars, so the rounding has to happen somewhere, and
 * doing it here rather than in the model's head means it is testable. The
 * direction is not cosmetic:
 *
 *   money she HAS   (left over, right now, plan on, income) → DOWN
 *   money she OWES  (out, spent, bills due)                 → UP
 *
 * Each figure moves by less than a dollar and always away from her overspending.
 * The opposite lean would have Kin overstate what is available by up to a dollar
 * on every number, which is the small version of the error this whole file is
 * shaped around.
 */
export function dollarsHave(n: number): number {
  return Math.floor(n);
}
export function dollarsOwe(n: number): number {
  return Math.ceil(n);
}

/** A failure, phrased so Kin can say it and carry on. Never thrown. */
export const VOICE_BUDGET_READ_FAILED =
  "You could not reach her budget just now — a hiccup on your end, not a sign she has none. " +
  "Tell her the lookup failed and you'll try again in a moment. " +
  "⚠️ Name NO amount: you were not given one, and a number you invent here is money she will go and spend.";

export interface VoiceBudgetBill {
  name: string;
  /** Whole dollars, rounded UP. */
  amount: number;
  /** "5th", "Last day", or "" when she never set one. */
  due: string;
  /** Falls in the month AFTER the one being reported. */
  nextMonth?: boolean;
}

export interface VoiceBudgetMonth {
  totalIn: number;
  totalOut: number;
  /** ⚠️ null when the read was capped. Then no leftover may be spoken. */
  leftOver: number | null;
  /** ⚠️ null when capped OR when no pay day is saved. Never a zero. */
  rightNow: number | null;
  planOn: number | null;
  billCount: number;
  /** "15th", or null when she has saved no pay day. */
  nextPayDay: string | null;
  /** That pay day falls next month. */
  nextPayDayWraps: boolean;
  /** At most `VOICE_BUDGET_NAMED`, soonest first. */
  dueBeforeNextPayDay: VoiceBudgetBill[];
  /** Matched but NOT named above, so "and N others" is sayable. */
  dueBeforeOthers: number;
  dueBeforeTotal: number;
  /** Every figure here is a FLOOR. */
  floor: boolean;
}

export interface VoiceBudgetProject {
  name: string;
  spent: number;
  /** ⚠️ null when she set no total, or when capped. NOT a zero. */
  left: number | null;
  total: number | null;
  floor: boolean;
  completed: boolean;
  /** More than one of her budgets has this name. */
  sharedName?: number;
}

export type VoiceBudgetOutcome =
  | {
      ok: true;
      month: VoiceBudgetMonth | null;
      projects: VoiceBudgetProject[];
      /** Budgets she has that were NOT named above. */
      projectsOthers: number;
      monthMissing: boolean;
      projectsMissing: boolean;
      monthUnreadable: boolean;
      projectsUnreadable: boolean;
      projectCountIsFloor: boolean;
      say: string;
    }
  | { ok: false; error: string }
  | {
      ok: false;
      miss: true;
      requested: string;
      available: string[];
      availableIncomplete: boolean;
      error: string;
    };

function monthView(p: PersonalBudgetView): VoiceBudgetMonth {
  const floor = p.capped;
  const named = p.dueBeforeNextPayDay.slice(0, VOICE_BUDGET_NAMED);
  return {
    totalIn: dollarsHave(p.totalIn),
    totalOut: dollarsOwe(p.totalOut),
    // ⚠️ The three suppressions. See the header: a capped read may not produce
    // a "what's left" figure at all, in any of its three forms.
    leftOver: floor ? null : dollarsHave(p.leftOver),
    rightNow: floor || p.rightNow === null ? null : dollarsHave(p.rightNow),
    planOn: floor || p.planOn === null ? null : dollarsHave(p.planOn),
    billCount: p.billCount,
    nextPayDay: p.nextPayDay === null ? null : dueDayLabel(p.nextPayDay),
    nextPayDayWraps: p.nextPayDayWraps,
    dueBeforeNextPayDay: named.map((b) => ({
      name: b.name,
      amount: dollarsOwe(b.amount),
      due: b.dueLabel,
      ...(b.nextMonth ? { nextMonth: true } : {}),
    })),
    // ⚠️ Both the render cap AND the fetch cap. `dueBeforeNextPayDayOmitted` is
    // already non-zero when chat's own 40-bill allowance ran out, so dropping it
    // and counting only my own slice would under-report the remainder.
    dueBeforeOthers:
      Math.max(0, p.dueBeforeNextPayDay.length - named.length) +
      p.dueBeforeNextPayDayOmitted,
    dueBeforeTotal: dollarsOwe(p.dueBeforeNextPayDayTotal),
    floor,
  };
}

function projectView(p: ProjectBudgetView): VoiceBudgetProject {
  const floor = p.capped;
  return {
    name: p.name,
    spent: dollarsOwe(p.spent),
    // ⚠️ null when she never set one, per ProjectBudgetView's doc comment: the
    // app renders `0 - spent` there, which is survivable next to an empty field
    // on screen and is NOT survivable as Kin saying "you're $400 over".
    total: p.total === null ? null : dollarsOwe(p.total),
    left: floor || p.total === null ? null : dollarsHave(p.total - p.spent),
    floor,
    completed: p.completed,
    ...(p.duplicateNameCount ? { sharedName: p.duplicateNameCount } : {}),
  };
}

/**
 * The spoken payload. Pure, so every rule above is testable without a database.
 */
export function buildVoiceBudget(outcome: BudgetReadOutcome): VoiceBudgetOutcome {
  if (outcome.kind === "failed") return { ok: false, error: VOICE_BUDGET_READ_FAILED };

  if (outcome.kind === "miss") {
    const names = outcome.available.filter((n) => n.trim());
    if (names.length === 0) {
      return {
        ok: false,
        miss: true,
        requested: outcome.requested,
        available: [],
        availableIncomplete: false,
        error:
          `She has no budget called "${outcome.requested}", and no project budgets at all yet. ` +
          "Say that plainly out loud and offer to help her start one in the app.",
      };
    }
    if (outcome.availableTruncated) {
      return {
        ok: false,
        miss: true,
        requested: outcome.requested,
        available: names.slice(0, VOICE_BUDGET_NAMED),
        availableIncomplete: true,
        error:
          `No budget called "${outcome.requested}" came back, but THE LIST IS INCOMPLETE — she has more. ` +
          "⚠️ Do NOT tell her that name isn't one of hers. Say you could only see some of them, " +
          "name one or two, and ask her to say the name again.",
      };
    }
    return {
      ok: false,
      miss: true,
      requested: outcome.requested,
      available: names.slice(0, VOICE_BUDGET_NAMED),
      availableIncomplete: names.length > VOICE_BUDGET_NAMED,
      error:
        `She has no budget called "${outcome.requested}". Tell her that and name one or two she does ` +
        `have so she can pick: ${names.slice(0, VOICE_BUDGET_NAMED).join(", ")}.`,
    };
  }

  const projects = outcome.projects.slice(0, VOICE_BUDGET_NAMED).map(projectView);
  const month = outcome.personal ? monthView(outcome.personal) : null;
  const floorAnywhere =
    (month?.floor ?? false) || outcome.projects.some((p) => p.capped);

  const rules: string[] = [
    "One to three sentences, whole dollars, and answer ONLY what she asked. Never read a list of bills or a table out loud.",
  ];

  // ⚠️ A half that failed is STATED, never omitted — silence about her monthly
  // budget while reporting projects reads as "there isn't one". Same rule as
  // renderBudgetRead's two *Failed branches.
  if (outcome.personalFailed) {
    rules.push(
      "⚠️ Her MONTHLY budget could not be read. Say so honestly if it's relevant, give NO monthly amount, and do NOT say she hasn't set one up.",
    );
  }
  if (outcome.projectsFailed) {
    rules.push(
      "⚠️ Her PROJECT budgets could not be read. Say so honestly if it's relevant, give NO project amount, and do NOT say she has none.",
    );
  }
  if (floorAnywhere) {
    rules.push(
      "⚠️ This read was TRUNCATED, so every number is a FLOOR: she has spent AT LEAST this much. " +
        "Say that plainly, state NO remaining amount (there isn't one here to state), and offer to have her open the app for the full picture.",
    );
  }
  if (month && month.rightNow === null && !month.floor) {
    rules.push(
      "⚠️ No pay day is saved, so there is no right-now figure. Do NOT treat that as zero — tell her she can add her pay day in the app and you'll be able to say it.",
    );
  }
  if (outcome.projectsTruncated) {
    rules.push(
      "⚠️ She has MORE project budgets than you were given. Never say how many she has, and never say she has no budget for something on the strength of this list.",
    );
  }
  if (!outcome.personal && !outcome.personalFailed) {
    rules.push(
      "She has no budget set up for this month. That is a real answer, not a failed read — say it plainly and never say you can't see her budget, because you just looked.",
    );
  }
  if (outcome.projects.length === 0 && !outcome.projectsFailed && !outcome.projectsTruncated) {
    rules.push("She has no project budgets at all — a real answer, not a failed read.");
  }
  rules.push(
    "⚠️ Never volunteer a number she didn't ask for. She may not be alone in the car.",
  );

  return {
    ok: true,
    month,
    projects,
    projectsOthers: Math.max(0, outcome.projects.length - projects.length),
    monthMissing: !outcome.personal && !outcome.personalFailed,
    projectsMissing:
      outcome.projects.length === 0 && !outcome.projectsFailed && !outcome.projectsTruncated,
    monthUnreadable: outcome.personalFailed,
    projectsUnreadable: outcome.projectsFailed,
    projectCountIsFloor: outcome.projectsTruncated,
    say: rules.join(" "),
  };
}

/**
 * Her money, shaped for speech.
 *
 * ⚠️ Never throws and never rejects. A tool handler that throws leaves the model
 * waiting on a function_call_output that is not coming and Kin goes silent
 * mid-call — see voiceWriteBudget.ts's header. Silence is how a voice assistant
 * reads as broken; a spoken "I couldn't pull it up" does not.
 *
 * ⚠️ A read failure is reported as a FAILURE, never as "she has no budget" and
 * never as a zero. Those are opposite answers and only one of them is safe to be
 * wrong about.
 */
export async function readVoiceBudget(
  client: VoiceBudgetClient,
  userId: string,
  requestedName = "",
  now: Date = new Date(),
): Promise<VoiceBudgetOutcome> {
  try {
    let tz = DEFAULT_USER_TIMEZONE;
    try {
      tz = await userTimeZone(client as any, userId);
    } catch (_) {
      // ⚠️ A zone lookup failure must not fail the whole answer. The fallback is
      // the same default budgetRead.ts uses, so the month key stays sane; it can
      // only matter within hours of a month boundary.
    }
    const outcome = await fetchBudgetRead(client as any, userId, requestedName, now, tz);
    return buildVoiceBudget(outcome);
  } catch (e) {
    console.error("[get_budget] voice read threw:", e);
    return { ok: false, error: VOICE_BUDGET_READ_FAILED };
  }
}
